import { useCallback } from 'react';

import type { ActionTemplate } from 'config/api/actionLibrary/actionLibrary.types';
import ROLES from 'config/constants/ROLES';
import userDataStorage from 'storages/userDataStorage';

/**
 * Returns whether the current user can toggle Active/Inactive status of a template.
 * For org templates: same as canEdit (Content Editor or AL Admin).
 * For PocketSafe (Global) templates: Content Editor can toggle; AL Admin can toggle
 * (local override for their org only - backend applies org-scoped status).
 * Used for AC 4.2: AL Admin can Activate/Deactivate Global templates (local override).
 */
const useCanToggleActiveActionTemplate = () => {
  const userData = userDataStorage.get() as { ps_roles?: string[]; roles?: number[] } | undefined;
  const { ps_roles = [], roles = [] } = userData || {};

  const isContentEditor = ps_roles.includes(ROLES.PS_ROLES.CONTENT_EDITOR);
  const isActionLibraryAdmin = roles.includes(ROLES.ORGANIZATION.ACTION_LIBRARY_ADMINISTRATOR);

  const canToggleActive = useCallback(
    (template: ActionTemplate) => {
      if (!template) return false;

      // Content Editors can toggle any template
      if (isContentEditor) return true;

      // Org-owned templates: AL Admin can toggle (same as canEdit)
      if (template.ownerType !== 'pocketsafe') return true;

      // PocketSafe (Global) template: AL Admin can toggle for local override (AC 4.2)
      if (isActionLibraryAdmin && template.ownerType === 'pocketsafe') return true;

      return false;
    },
    [isContentEditor, isActionLibraryAdmin],
  );

  return canToggleActive;
};

export default useCanToggleActiveActionTemplate;
